import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, ArrowLeft, FileText, Upload, ClipboardPaste, ScanSearch } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import ComplianceDisclaimer from "../components/ComplianceDisclaimer";
import RequirementEditor from "../components/Screening/RequirementEditor";
import ScreeningBatchPanel from "../components/Screening/ScreeningBatchPanel";

const STROKE = 1.5;

function WhatThisIsPanel() {
  return (
    <div className="hp-card p-4" data-testid="screening-what-this-is">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <div className="hp-section-label text-emerald-800">What this is</div>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-600">
            A check of which JD requirements have supporting evidence in each CV, with the exact
            evidence quoted. The requirement-match rate is arithmetic over the requirements you define.
          </p>
        </div>
        <div>
          <div className="hp-section-label text-ink-500">What this isn&rsquo;t</div>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-600">
            An assessment of candidate quality, ability, or fit. Hiretape does not evaluate,
            rank, or recommend candidates. You decide what the evidence means.
          </p>
        </div>
      </div>
      <div className="mt-3 border-t border-ink-100 pt-3">
        <ComplianceDisclaimer />
      </div>
    </div>
  );
}

function JobDescriptionForm({ roleId, roleTitle, onCreated }) {
  const [mode, setMode] = useState("paste");
  const [title, setTitle] = useState(roleTitle ? `${roleTitle} — JD` : "Job description");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (mode === "paste" && !text.trim()) {
      toast.error("Paste the job description text.");
      return;
    }
    if (mode === "upload" && !file) {
      toast.error("Choose a PDF or DOCX file.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("role_id", roleId);
      fd.append("title", title.trim() || "Job description");
      fd.append("source_type", mode);
      if (mode === "paste") fd.append("raw_text", text);
      if (mode === "upload") fd.append("file", file);
      const { data } = await api.post(`/job-descriptions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Job description saved.");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the job description.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hp-card p-5" data-testid="jd-form">
      <div className="mb-3 flex items-center gap-2">
        <FileText className="h-4 w-4 text-emerald-800" strokeWidth={STROKE} />
        <span className="text-[14px] font-medium text-ink-900">Add the job description</span>
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="mb-3 w-full rounded-md border border-ink-200 px-3 py-2 text-[13px] focus:border-emerald-800/40 focus:outline-none"
        placeholder="JD title"
        data-testid="jd-title-input"
      />
      <div className="mb-3 inline-flex overflow-hidden rounded-md border border-ink-200">
        {[["paste", "Paste text", ClipboardPaste], ["upload", "Upload file", Upload]].map(([v, lbl, Icon]) => (
          <button
            key={v}
            onClick={() => setMode(v)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-medium transition ${
              mode === v ? "bg-emerald-800 text-white" : "bg-white text-ink-600 hover:bg-ink-50"
            }`}
            data-testid={`jd-mode-${v}`}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={STROKE} /> {lbl}
          </button>
        ))}
      </div>
      {mode === "paste" ? (
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder="Paste the full job description here…"
          className="w-full rounded-md border border-ink-200 px-3 py-2 text-[13px] leading-relaxed focus:border-emerald-800/40 focus:outline-none"
          data-testid="jd-text-input"
        />
      ) : (
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-ink-300 px-3 py-8 text-[13px] text-ink-500 hover:border-emerald-800/40" data-testid="jd-file-label">
          <Upload className="h-4 w-4" strokeWidth={STROKE} />
          {file ? file.name : "Choose a PDF or DOCX (max 10MB)"}
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="jd-file-input"
          />
        </label>
      )}
      <div className="mt-4">
        <button
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-md bg-emerald-800 px-4 py-2 text-[13px] font-medium text-white hover:bg-emerald-900 disabled:opacity-60"
          data-testid="jd-save-button"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} /> : <FileText className="h-4 w-4" strokeWidth={STROKE} />}
          {busy ? "Saving…" : "Save job description"}
        </button>
      </div>
    </div>
  );
}

export default function RoleScreening() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const { member } = useAuth();
  const canEdit = ["recruiter", "admin"].includes(member?.role);

  const [role, setRole] = useState(null);
  const [jd, setJd] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [err, setErr] = useState("");

  const loadRequirements = useCallback(async (jdId) => {
    const { data } = await api.get(`/job-descriptions/${jdId}/requirements`);
    setRequirements(data);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: roleData }, { data: jds }] = await Promise.all([
          api.get(`/roles/${roleId}`),
          api.get(`/roles/${roleId}/job-descriptions`),
        ]);
        setRole(roleData);
        if (jds.length > 0) {
          setJd(jds[0]);
          await loadRequirements(jds[0].job_description_id);
        }
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load screening");
      } finally {
        setLoading(false);
      }
    })();
  }, [roleId, loadRequirements]);

  const onJdCreated = async (created) => {
    setJd(created);
    setRequirements([]);
  };

  const extract = async () => {
    setExtracting(true);
    try {
      const { data } = await api.post(`/job-descriptions/${jd.job_description_id}/extract-requirements`);
      setRequirements(data);
      toast.success(
        data.length > 0
          ? `Extracted ${data.length} requirement${data.length === 1 ? "" : "s"} — review and confirm below.`
          : "No objective requirements found. Add them manually below.",
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed.");
    } finally {
      setExtracting(false);
    }
  };

  if (loading) {
    return <AppShell><div className="flex justify-center p-12"><Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={STROKE} /></div></AppShell>;
  }
  if (err) {
    return <AppShell><div className="hp-card p-6 text-red-700" data-testid="screening-error">{err}</div></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="role-screening-page">
        <header>
          <button
            onClick={() => navigate(`/roles/${roleId}`)}
            className="inline-flex items-center gap-1 text-[13px] text-ink-500 hover:text-ink-900"
            data-testid="back-to-role"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={STROKE} /> Back to role
          </button>
          <h1 className="font-display mt-3 flex items-center gap-2 text-3xl font-medium tracking-tight text-ink-900">
            <ScanSearch className="h-6 w-6 text-emerald-800" strokeWidth={STROKE} />
            Screen CVs
          </h1>
          <p className="mt-1 text-[13px] text-ink-500">
            {role?.title}
            {jd ? <> · {requirements.length} requirement{requirements.length === 1 ? "" : "s"}</> : null}
          </p>
        </header>

        <WhatThisIsPanel />

        {!jd ? (
          canEdit ? (
            <JobDescriptionForm roleId={roleId} roleTitle={role?.title} onCreated={onJdCreated} />
          ) : (
            <div className="hp-card p-6 text-[13px] text-ink-500" data-testid="jd-none-readonly">
              No job description has been added for this role yet.
            </div>
          )
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-ink-200 bg-ink-50/60 px-3 py-2" data-testid="jd-current">
              <div className="flex items-center gap-2 text-[13px] text-ink-700">
                <FileText className="h-4 w-4 text-ink-400" strokeWidth={STROKE} />
                <span className="font-medium">{jd.title}</span>
                <span className="text-ink-400">·</span>
                <span className="text-[12px] text-ink-500 capitalize">{jd.source_type}</span>
              </div>
              {canEdit && (
                <button
                  onClick={() => { setJd(null); setRequirements([]); }}
                  className="text-[12px] text-ink-500 underline-offset-2 hover:text-emerald-800 hover:underline"
                  data-testid="jd-replace"
                  title="Re-extraction needs a fresh JD — this starts over without deleting the current requirements"
                >
                  Use a different JD to re-extract
                </button>
              )}
            </div>

            <RequirementEditor
              jobDescriptionId={jd.job_description_id}
              requirements={requirements}
              canEdit={canEdit}
              onMutated={() => loadRequirements(jd.job_description_id)}
              onExtract={extract}
              extracting={extracting}
            />

            <div className="border-t border-ink-100 pt-6">
              <ScreeningBatchPanel
                jobDescriptionId={jd.job_description_id}
                roleId={roleId}
                activeRequirementCount={requirements.filter((r) => r.is_active).length}
                canEdit={canEdit}
                canDelete={member?.role === "admin"}
              />
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
